import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { Building2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token — request a new link.");
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200 p-8 shadow-sm" data-testid="reset-password-card">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 rounded-xl bg-[#C05640] text-white grid place-items-center">
            <Building2 size={20} />
          </div>
          <div>
            <div className="font-serif-display text-xl font-semibold leading-none">NivasOne</div>
            <div className="text-[11px] text-stone-500">Apartment OS</div>
          </div>
        </div>

        {done ? (
          <div className="text-center py-4" data-testid="reset-success">
            <CheckCircle2 size={40} className="text-emerald-600 mx-auto" />
            <h2 className="font-serif-display text-2xl font-semibold mt-3">Password updated</h2>
            <p className="text-sm text-stone-500 mt-1">Redirecting to sign in…</p>
          </div>
        ) : (
          <>
            <h2 className="font-serif-display text-2xl font-semibold">Set a new password</h2>
            <p className="text-sm text-stone-500 mt-1">
              Enter the new password you&apos;d like to use for your NivasOne account.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="reset-form">
              <div>
                <Label htmlFor="np">New password</Label>
                <Input id="np" type="password" required minLength={6} value={password}
                       onChange={(e) => setPassword(e.target.value)}
                       data-testid="reset-new-password" placeholder="At least 6 characters" className="h-12 mt-1.5" />
              </div>
              <div>
                <Label htmlFor="np2">Confirm new password</Label>
                <Input id="np2" type="password" required minLength={6} value={confirm}
                       onChange={(e) => setConfirm(e.target.value)}
                       data-testid="reset-confirm-password" className="h-12 mt-1.5" />
              </div>
              <Button type="submit" disabled={busy || !token} data-testid="reset-submit"
                      className="w-full h-12 rounded-full bg-[#C05640] hover:bg-[#A54734] text-white font-semibold">
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          </>
        )}

        <p className="text-xs text-stone-500 mt-6 text-center">
          <Link to="/login" className="hover:text-[#C05640]" data-testid="reset-back-login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
